import { useRef, useCallback, useEffect } from "react";

import { debounce } from "lodash";

import { useLiveFormEditorContext } from "../common/LiveFormEditorContext";

import { getScrollTopStepTarget, ScrollContext } from "./getScrollTopStepTarget";

import { useArrow } from "@/components/LiveEditorElements/AnimatePreview/Arrow/ArrowContext";

export function useScrollPreviewWithArrow() {
  const { currentStepKey } = useLiveFormEditorContext();
  const { refreshArrowPosition, showArrow, userHasScrolled } = useArrow();

  const isPreviewAlreadyScrolledToTargetStep = useRef(false);
  const pendingScrollsRef = useRef(0);

  const handleScrollEnd = useCallback(
    (iframe: HTMLIFrameElement, context?: ScrollContext) => {
      return () => {
        pendingScrollsRef.current--;

        if (pendingScrollsRef.current === 0) {
          // Wait for scroll animation to fully settle
          setTimeout(() => {
            if (context?.targetElementId && !iframe.contentDocument?.getElementById(context.targetElementId)) {
              return;
            }
            refreshArrowPosition(context);
            showArrow();
          }, 300);
        }

        isPreviewAlreadyScrolledToTargetStep.current = false;
        iframe.contentDocument?.removeEventListener("scrollend", handleScrollEnd(iframe));
        userHasScrolled.current = false;
      };
    },
    [refreshArrowPosition, showArrow, userHasScrolled]
  );

  const scrollAndUpdateArrow = useCallback(
    (context?: ScrollContext) => {
      // Only for the description step, we don't want to scroll automatically to the target element if the user has scrolled in the preview
      if (userHasScrolled.current && currentStepKey === "description") {
        return;
      }

      if (pendingScrollsRef.current > 0) {
        return;
      }

      if (isPreviewAlreadyScrolledToTargetStep.current) {
        return;
      }

      const iframe = document.getElementById("preview-iframe") as HTMLIFrameElement;
      if (!iframe?.contentWindow) {
        return;
      }

      let scrollTopTarget = getScrollTopStepTarget(iframe, currentStepKey, context);

      if (scrollTopTarget === null) {
        isPreviewAlreadyScrolledToTargetStep.current = false;
        return;
      }

      const maxScrollTop =
        iframe.contentDocument?.documentElement?.scrollHeight !== undefined
          ? iframe.contentDocument.documentElement.scrollHeight - iframe.contentWindow.innerHeight
          : undefined;

      if (maxScrollTop !== undefined) {
        // Clamp the scroll target to the scrollable range
        scrollTopTarget = Math.max(0, Math.min(scrollTopTarget, maxScrollTop));
      }

      const currentScrollTop = iframe.contentWindow.scrollY;
      if (Math.abs(currentScrollTop - scrollTopTarget) < 1) {
        refreshArrowPosition(context);
        showArrow();
        isPreviewAlreadyScrolledToTargetStep.current = false;
        userHasScrolled.current = false;
        return;
      }

      pendingScrollsRef.current++;
      isPreviewAlreadyScrolledToTargetStep.current = true;

      iframe.contentWindow.scrollTo({
        top: scrollTopTarget,
        behavior: "smooth",
      });

      iframe.contentDocument?.addEventListener("scrollend", handleScrollEnd(iframe, context), {
        once: true,
      });
    },
    [currentStepKey, handleScrollEnd, refreshArrowPosition, showArrow, userHasScrolled]
  );

  const scrollAndUpdateArrowRef = useRef(scrollAndUpdateArrow);

  useEffect(() => {
    scrollAndUpdateArrowRef.current = scrollAndUpdateArrow;
  }, [scrollAndUpdateArrow]);

  const debouncedScrollAndUpdateArrow = useRef(
    debounce((context?: ScrollContext) => {
      scrollAndUpdateArrowRef.current(context);
    }, 100)
  ).current;

  return { scrollAndUpdateArrow: debouncedScrollAndUpdateArrow };
}
